import { useEffect, useState } from 'react';

import { Api } from '../api/api';
import { API_BASE_URL } from '../api/client';
import type { OauthProvider } from '../api/models';
import { PublicRoute } from '../navigation/routes';
import { Strings } from '../strings';
import { Type } from '../theme/typography';

/**
 * Sign in with a provider.
 *
 * **The buttons come from the API, not from a build flag.** `GET /auth/providers` says which are
 * configured, and a deployment with neither renders nothing at all — no divider, no gap. A button present
 * because a build argument said so, against an API where the provider is not set up, is a button that
 * fails, and it would fail differently on each of the three clients (R0148).
 *
 * The marks are drawn rather than fetched: both providers' brand assets are served from their own CDNs,
 * and a sign-in button that depends on a third party being reachable is a sign-in button that sometimes
 * is not there.
 */
export function ProviderButtons({ label }: { label: string }) {
  const [providers, setProviders] = useState<OauthProvider[]>([]);

  useEffect(() => {
    let cancelled = false;

    void Api.providers().then((result) => {
      if (!cancelled && result.ok) {
        setProviders(result.value);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  if (providers.length === 0) {
    return null;
  }

  // A full page load, not a route change: the next stop is the provider's own domain.
  const start = (provider: OauthProvider): void => {
    const target = `${window.location.origin}${PublicRoute.oauthCallback}`;

    window.location.assign(`${API_BASE_URL}/auth/${provider}?redirect=${encodeURIComponent(target)}`);
  };

  return (
    <div style={{ width: '100%' }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', margin: 'var(--space-4) 0' }}
      >
        <div style={{ height: 1, flex: 1, background: 'var(--border-default)' }} />
        <span style={{ ...Type.bodyS, color: 'var(--text-secondary)' }}>{label}</span>
        <div style={{ height: 1, flex: 1, background: 'var(--border-default)' }} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        {providers.includes('google') ? (
          <ProviderButton onClick={() => start('google')} label={Strings.signInWithGoogle}>
            <GoogleMark />
          </ProviderButton>
        ) : null}
        {providers.includes('facebook') ? (
          <ProviderButton onClick={() => start('facebook')} label={Strings.signInWithFacebook}>
            <FacebookMark />
          </ProviderButton>
        ) : null}
      </div>
    </div>
  );
}

/**
 * One button, in the shape people recognise: the mark on the left, the words centred, a light surface and
 * a hairline border. Both providers' own guidance describes the same shape, and a child's parent has seen
 * it a hundred times elsewhere.
 */
function ProviderButton({
  onClick,
  label,
  children,
}: {
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        width: '100%',
        minHeight: 'var(--touch-parent)',
        padding: '0 var(--space-4)',
        borderRadius: 'var(--radius-m)',
        border: '1px solid var(--border-default)',
        background: 'var(--surface-raised)',
        color: 'var(--text-primary)',
        cursor: 'pointer',
        ...Type.label,
      }}
    >
      {children}
      <span style={{ flex: 1, textAlign: 'center' }}>{label}</span>
      {/* Balances the mark, so the words sit in the middle of the button rather than left of it. */}
      <span style={{ width: 20 }} aria-hidden="true" />
    </button>
  );
}

function GoogleMark() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5Z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65Z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59A14.5 14.5 0 0 1 9.77 24c0-1.6.27-3.15.76-4.59l-7.98-6.19A23.94 23.94 0 0 0 0 24c0 3.88.93 7.54 2.56 10.78l7.97-6.19Z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48Z"
      />
    </svg>
  );
}

function FacebookMark() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="#1877F2"
        d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
      />
    </svg>
  );
}
